/**
 * P2-04 · Pure card state logic, kept out of the components so the precedence
 * contract is unit-testable on its own.
 *
 * Exactly ONE status badge renders, chosen by the strict total order fixed in
 * P2-DB04 r2 §02: out-of-stock › sale › low-stock › new. Low-stock and new
 * only exist while the item is buyable, so neither can coexist with sold-out.
 */
import { LOW_STOCK_THRESHOLD } from "~/config/storefront";
import { formatMoney, isOnSale } from "~/lib/money";

export type CardStatus = "sold-out" | "sale" | "low-stock" | "new" | null;

export interface CardStatusInput {
  inStock: boolean;
  onSale: boolean;
  totalStock: number;
  isNew: boolean;
}

export function deriveCardStatus(
  input: CardStatusInput,
  lowStockThreshold: number = LOW_STOCK_THRESHOLD,
): CardStatus {
  if (!input.inStock) return "sold-out";
  if (input.onSale) return "sale";
  if (input.totalStock > 0 && input.totalStock <= lowStockThreshold) {
    return "low-stock";
  }
  if (input.isNew) return "new";
  return null;
}

export interface CardNameInput {
  title: string;
  priceMinMinor: number | null;
  priceMaxMinor: number | null;
  compareAtMinMinor: number | null;
  currency: string;
}

/** True when the card should render a "From …" range price. */
export function hasPriceRange(input: {
  priceMinMinor: number | null;
  priceMaxMinor: number | null;
}): boolean {
  return (
    input.priceMinMinor !== null &&
    input.priceMaxMinor !== null &&
    input.priceMaxMinor > input.priceMinMinor
  );
}

/**
 * The card link's accessible name — title + price in one stop (P2-DB04 §01),
 * with the "was" reading appended on sale. Built from the base product, never
 * the transient swatch preview (r2 §03: the name doesn't change on preview).
 */
export function cardAccessibleName(input: CardNameInput): string {
  if (input.priceMinMinor === null) return input.title;
  const from = hasPriceRange(input) ? "from " : "";
  const price = formatMoney(input.priceMinMinor, input.currency);
  const name = `${input.title}, ${from}${price}`;
  if (isOnSale(input.priceMinMinor, input.compareAtMinMinor)) {
    return `${name}, was ${formatMoney(input.compareAtMinMinor!, input.currency)}`;
  }
  return name;
}
